import { useEffect, useRef } from 'react'

export interface StreamEvent<T = unknown> { type: string; id: string; ts: string; data: T }

// One shared EventSource to /api/v2/stream for the whole tab, opened by the first
// subscriber and closed when the last one unmounts (EventSource reconnects on its own).
type Handler = (e: StreamEvent) => void
let es: EventSource | null = null
const handlers = new Map<string, Set<Handler>>()
const attached = new Set<string>()

function ensure(type: string) {
  if (typeof EventSource === 'undefined') return
  if (closeTimer) { clearTimeout(closeTimer); closeTimer = null }
  if (!es) { es = new EventSource('/api/v2/stream', { withCredentials: true }); attached.clear() }
  if (attached.has(type)) return
  attached.add(type)
  es.addEventListener(type, (ev: Event) => {
    const msg = ev as MessageEvent<string>
    let body: { ts: string; data: unknown }
    try { body = JSON.parse(msg.data) as { ts: string; data: unknown } } catch { return }
    handlers.get(type)?.forEach(h => h({ type, id: msg.lastEventId, ts: body.ts, data: body.data }))
  })
}

// Close a few seconds after the last subscriber leaves, so page-to-page navigation keeps the connection.
let closeTimer: ReturnType<typeof setTimeout> | null = null
function release() {
  for (const set of handlers.values()) if (set.size) return
  if (closeTimer) clearTimeout(closeTimer)
  closeTimer = setTimeout(() => {
    closeTimer = null
    for (const set of handlers.values()) if (set.size) return
    es?.close(); es = null; attached.clear()
  }, 5000)
}

/**
 * Subscribe to /api/v2/stream (SSE). All hooks share one connection; the handler
 * ref keeps the latest closure without re-subscribing.
 */
export function useStream(types: string[], handler: (e: StreamEvent) => void, enabled = true) {
  const ref = useRef(handler)
  useEffect(() => { ref.current = handler }, [handler])
  const key = types.join(',')
  useEffect(() => {
    if (!enabled) return
    const fn: Handler = e => ref.current(e)
    const list = key.split(',')
    for (const type of list) {
      if (!handlers.has(type)) handlers.set(type, new Set())
      handlers.get(type)?.add(fn)
      ensure(type)
    }
    return () => { for (const type of list) handlers.get(type)?.delete(fn); release() }
  }, [key, enabled])
}
